/**
 * Loading Store
 * Reactive state management for loading UI
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export enum LoadingPhase {
  BOOTSTRAP = 'bootstrap',
  APP_READY = 'app_ready',
  PAGE_LOADING = 'page_loading',
  PAGE_READY = 'page_ready',
  IDLE = 'idle',
  RECOVERING = 'recovering'
}

export interface LoadingMetrics {
  phase: LoadingPhase;
  startTime: number;
  endTime?: number;
  duration?: number;
  context?: string;
}

export interface LoadingReport {
  avgBootstrapTime: number;
  avgPageLoadTime: number;
  slowestLoads: LoadingMetrics[];
  totalLoads: number;
}

export const useLoadingStore = defineStore('loading', () => {
  // State
  const phase = ref<LoadingPhase>(LoadingPhase.BOOTSTRAP);
  const progress = ref(0);
  const message = ref('');
  const subMessage = ref('');
  const showLoadingUI = ref(true);
  const minimumLoadingTime = ref(import.meta.env.DEV ? 0 : 300); // ms
  const showIdleWarning = ref(false);
  const showIdleOverlay = ref(false);
  const idleWarningMessage = ref('Your session will expire in 2 minutes due to inactivity');
  const errorMessage = ref('');
  const showError = ref(false);

  // Metrics tracking
  const loadingMetrics = ref<Map<string, LoadingMetrics>>(new Map());
  const performanceEnabled = ref(import.meta.env.DEV);

  // Computed
  const isBootstrapping = computed(() => phase.value === LoadingPhase.BOOTSTRAP);
  
  const isAppReady = computed(() => 
    ![LoadingPhase.BOOTSTRAP].includes(phase.value)
  );
  
  const isPageReady = computed(() => 
    [LoadingPhase.APP_READY, LoadingPhase.PAGE_READY].includes(phase.value)
  );
  
  const isLoading = computed(() => 
    [LoadingPhase.BOOTSTRAP, LoadingPhase.PAGE_LOADING, LoadingPhase.RECOVERING].includes(phase.value)
  );
  
  const isIdle = computed(() => phase.value === LoadingPhase.IDLE);
  
  const isRecovering = computed(() => phase.value === LoadingPhase.RECOVERING);

  const loadingText = computed(() => {
    if (message.value) return message.value;
    
    switch (phase.value) {
      case LoadingPhase.BOOTSTRAP:
        return 'Initializing application...';
      case LoadingPhase.PAGE_LOADING:
        return 'Loading page...';
      case LoadingPhase.RECOVERING:
        return 'Refreshing your session...';
      default:
        return 'Loading...';
    }
  });

  const loadingProgress = computed(() => {
    if (progress.value > 0) return progress.value;
    
    // Default progress based on phase
    switch (phase.value) {
      case LoadingPhase.BOOTSTRAP:
        return 20;
      case LoadingPhase.APP_READY:
        return 50;
      case LoadingPhase.PAGE_LOADING:
        return 70;
      case LoadingPhase.PAGE_READY:
        return 100;
      case LoadingPhase.RECOVERING:
        return 30;
      default:
        return 0;
    }
  });

  // Actions
  function setPhase(newPhase: LoadingPhase) {
    const oldPhase = phase.value;
    phase.value = newPhase;
    
    // Track phase transitions
    if (performanceEnabled.value) {
      trackPhaseTransition(oldPhase, newPhase);
    }
    
    // Update UI state based on phase
    updateUIState(newPhase);
    
    // Auto-hide error on phase change
    if (showError.value && newPhase !== LoadingPhase.BOOTSTRAP) {
      hideError();
    }
  }

  function updateUIState(phase: LoadingPhase) {
    switch (phase) {
      case LoadingPhase.BOOTSTRAP:
        message.value = 'Initializing application...';
        subMessage.value = 'Please wait while we set things up';
        progress.value = 10;
        showLoadingUI.value = true;
        break;
        
      case LoadingPhase.APP_READY:
        message.value = '';
        subMessage.value = '';
        progress.value = 100;
        showLoadingUI.value = false;
        break;
        
      case LoadingPhase.PAGE_LOADING:
        message.value = 'Loading page data...';
        subMessage.value = '';
        progress.value = 50;
        showLoadingUI.value = true;
        break;
        
      case LoadingPhase.PAGE_READY:
        message.value = '';
        subMessage.value = '';
        progress.value = 100;
        showLoadingUI.value = false;
        break;
        
      case LoadingPhase.IDLE:
        showIdleOverlay.value = true;
        showLoadingUI.value = false;
        break;
        
      case LoadingPhase.RECOVERING:
        message.value = 'Welcome back!';
        subMessage.value = 'Refreshing your session...';
        progress.value = 30;
        showLoadingUI.value = true;
        showIdleOverlay.value = false;
        showIdleWarning.value = false;
        break;
    }
  }

  function setMessage(msg: string, subMsg?: string) {
    message.value = msg;
    if (subMsg !== undefined) {
      subMessage.value = subMsg;
    }
  }

  function setProgress(value: number) {
    progress.value = Math.min(100, Math.max(0, value));
  }

  function incrementProgress(amount: number = 10) {
    setProgress(progress.value + amount);
  }

  function setIdleWarning(show: boolean) {
    showIdleWarning.value = show;
  }

  function setIdleOverlay(show: boolean) {
    showIdleOverlay.value = show;
    if (!show) {
      showIdleWarning.value = false;
    }
  }

  function setError(error: string) {
    errorMessage.value = error;
    showError.value = true;
  }

  function clearError() {
    errorMessage.value = '';
    showError.value = false;
  }

  // Convenience methods for better API
  function showIdleWarningState() {
    setIdleWarning(true);
  }

  function hideIdleWarning() {
    setIdleWarning(false);
  }

  function showIdleOverlayState() {
    setIdleOverlay(true);
  }

  function hideIdleOverlay() {
    setIdleOverlay(false);
  }

  function showErrorState(error: string) {
    setError(error);
  }

  function hideError() {
    clearError();
  }

  function setMinimumLoadingTime(time: number) {
    minimumLoadingTime.value = time;
  }

  // Performance tracking
  function trackPhaseTransition(from: LoadingPhase, to: LoadingPhase) {
    const key = `${from}->${to}`;
    const now = performance.now();
    
    const existing = loadingMetrics.value.get(key);
    if (existing && !existing.endTime) {
      existing.endTime = now;
      existing.duration = now - existing.startTime;
    } else {
      loadingMetrics.value.set(key, {
        phase: to,
        startTime: now,
        context: key
      });
    }
  }

  function recordLoadingStart(context: string) {
    if (!performanceEnabled.value) return;
    
    loadingMetrics.value.set(context, {
      phase: phase.value,
      startTime: performance.now(),
      context
    });
  }

  function recordLoadingEnd(context: string) {
    if (!performanceEnabled.value) return;
    
    const metric = loadingMetrics.value.get(context);
    if (metric && !metric.endTime) {
      metric.endTime = performance.now();
      metric.duration = metric.endTime - metric.startTime;
      
      // Log slow operations
      if (metric.duration > 1000) {
        console.warn(`⚠️ Slow loading detected: ${context} took ${metric.duration.toFixed(0)}ms`);
      }
    }
  }

  function getLoadingReport(): LoadingReport {
    const metrics = Array.from(loadingMetrics.value.values())
      .filter(m => m.duration !== undefined);
    
    const bootstrapMetrics = metrics.filter(m => m.phase === LoadingPhase.BOOTSTRAP);
    const pageLoadMetrics = metrics.filter(m => m.phase === LoadingPhase.PAGE_LOADING);
    
    const avgBootstrapTime = bootstrapMetrics.length > 0
      ? bootstrapMetrics.reduce((sum, m) => sum + (m.duration || 0), 0) / bootstrapMetrics.length
      : 0;
      
    const avgPageLoadTime = pageLoadMetrics.length > 0
      ? pageLoadMetrics.reduce((sum, m) => sum + (m.duration || 0), 0) / pageLoadMetrics.length
      : 0;
    
    const slowestLoads = [...metrics]
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .slice(0, 5);
    
    return {
      avgBootstrapTime,
      avgPageLoadTime,
      slowestLoads,
      totalLoads: metrics.length
    };
  }

  function clearMetrics() {
    loadingMetrics.value.clear();
  }

  function reset() {
    phase.value = LoadingPhase.BOOTSTRAP;
    progress.value = 0;
    message.value = '';
    subMessage.value = '';
    showLoadingUI.value = true;
    showIdleWarning.value = false;
    showIdleOverlay.value = false;
    errorMessage.value = '';
    showError.value = false;
    loadingMetrics.value.clear();
  }

  return {
    // State
    phase,
    progress,
    message,
    subMessage,
    showLoadingUI,
    minimumLoadingTime,
    idleWarningVisible: showIdleWarning,
    idleOverlayVisible: showIdleOverlay,
    idleWarningMessage,
    errorMessage,
    errorVisible: showError,
    
    // Computed
    isBootstrapping,
    isAppReady,
    isPageReady,
    isLoading,
    isIdle,
    isRecovering,
    loadingText,
    loadingProgress,
    
    // Actions
    setPhase,
    setMessage,
    setProgress,
    incrementProgress,
    setIdleWarning,
    setIdleOverlay,
    setError,
    clearError,
    setMinimumLoadingTime,
    
    // Convenience methods
    showIdleWarning: showIdleWarningState,
    hideIdleWarning,
    showIdleOverlay: showIdleOverlayState,
    hideIdleOverlay,
    showError: showErrorState,
    hideError,
    
    // Performance
    recordLoadingStart,
    recordLoadingEnd,
    getLoadingReport,
    clearMetrics,
    
    // Utility
    reset
  };
});

// Export type for use in components
export type LoadingStore = ReturnType<typeof useLoadingStore>;